import React, { useMemo, useState } from 'react';
import { useConditionalEffect } from 'src/commons';
import { useAdHocQueries, useAdHocQuery, useAdHocQueryOutput, useAlias, useGroupName } from 'src/data/redux';
import { HammerstoneAppLayout, LinkButton, transformTo } from '../helpers';
import {
	Alert,
	Box,
	Button,
	ButtonDropdown,
	ColumnLayout,
	Container,
	ExpandableSection,
	Header,
	Popover,
	SpaceBetween,
	Table,
	Toggle,
} from '@amzn/awsui-components-console';
import { fetchAdHocQueries, fetchAdHocQuery, fetchAdHocQueryOutput } from 'src/data/api/fetchFromAPI';
import Content from '../helpers/content';
import { DisplayMode } from '../helpers/content/contentInterfaces';
import { AdHocContainerLayout, putAdHocQueryWithFlashbar } from './AdHocQuery';
import { useNavigate } from 'react-router-dom';
import PAGES from 'src/nav/Pages';
import { urlSearchString } from 'src/nav/navHelper';
import LogsNavigator from 'src/components/helpers/LogsNavigator';
import { AdHocQueryOutputData } from '@amzn/aws-hammerstone-exposed-restful-service-typescript-client/clients/hammerstoneexposedrestfulservicelambda';
import keys from 'src/constants/hammerstoneConstantKeys';

function isQueryRunning(statusCode: string) {
	return statusCode == 'ENQUEUED' || statusCode == 'RUNNING';
}

function isQueryCompleted(statusCode: string) {
	return statusCode == 'SUCCESS' || statusCode == 'FAILURE';
}

export default function ViewAdHocQuery() {
	const groupName = useGroupName();
	const userAlias = useAlias();

	const { data: adHocQueries, fetching } = useAdHocQueries(groupName);
	const currentQuery = adHocQueries?.at(0) ?? {};
	const { queryId } = currentQuery;
	useConditionalEffect(() => {
		fetchAdHocQueries({ groupName, userAlias }, true);
	}, [groupName, userAlias]);

	return (
		<HammerstoneAppLayout {...PAGES.VIEW_AD_HOC_QUERY.pageLayoutProps({ groupName })}>
			<SpaceBetween size="xl">
				<Header
					variant="h1"
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							<LinkButton
								iconName="add-plus"
								disabled={!groupName}
								href={PAGES.CREATE_AD_HOC_QUERY.path + urlSearchString({ groupName })}
							>
								Create
							</LinkButton>
						</SpaceBetween>
					}
				>
					Ad hoc queries
				</Header>
				{adHocQueries?.length === 0 ? (
					<Container header={`No queries for group ${groupName}`}>
						No ad hoc queries were found for user {userAlias} in group {groupName}. Click the "Create"
						button above to make a new ad hoc query against your cluster, or select a different Hammerstone
						group from the dropdown above.
					</Container>
				) : (
					<>
						<AdHocQueryConfigContainer loading={!groupName || fetching} queryId={queryId} />
						<AdHocQueryOutputContainer queryId={queryId} />
					</>
				)}
			</SpaceBetween>
		</HammerstoneAppLayout>
	);
}

function AdHocQueryConfigContainer(props: { queryId: string; loading?: boolean }) {
	const { queryId, loading } = props;
	const groupName = useGroupName();
	const userAlias = useAlias();
	const [isExecuting, setIsExecuting] = useState(false);
	const { data: adHocQuery, fetching } = useAdHocQuery(queryId);
	const navigate = useNavigate();

	const mode = !queryId || loading || fetching ? DisplayMode.Loading : DisplayMode.View;
	const contentProps = {
		resourceType: 'adHocQuery',
		resourceId: queryId,
		mode,
	} as const;
	const onRefresh = () => fetchAdHocQuery({ groupName, userAlias, queryId }, true);
	const [autoRefresh, setAutoRefresh] = useState<boolean>(true);
	useConditionalEffect(() => {
		if (isQueryRunning(adHocQuery?.statusCode)) {
			console.log('Auto-refresh enabled, scheduling refresh');
			const timeout = setTimeout(() => {
				console.log('Executing auto-refresh');
				onRefresh();
			}, 3_000);
			return () => clearTimeout(timeout);
		}
	}, [autoRefresh, adHocQuery]);

	useConditionalEffect(() => {
		onRefresh();
	}, [!loading, groupName, userAlias, queryId]);

	if (!loading && !queryId) {
		return (
			<Container header="Query not found">
				No ad hoc query with query {queryId} was found for user {userAlias} in group {groupName}.
			</Container>
		);
	}
	return (
		<Container
			header={
				<Header
					variant="h2"
					actions={
						<SpaceBetween direction="horizontal" size="xs">
							<Button
								iconName="play"
								disabled={adHocQuery?.statusCode !== 'CREATED'}
								disabledReason="Only query with CREATED status may be executed."
								loading={loading || fetching || isExecuting}
								onClick={async () => {
									setIsExecuting(true);
									putAdHocQueryWithFlashbar({
										queryId,
										userAlias,
										groupName,
										body: { operation: 'execute' },
									}).finally(() => {
										setIsExecuting(false);
										onRefresh();
									});
								}}
							>
								Execute
							</Button>
							<Box margin={{ top: 'xxs', right: 'xs', left: 'xs' }}>
								<Toggle
									disabled={!isQueryRunning(adHocQuery?.statusCode)}
									checked={autoRefresh}
									onChange={({ detail }) => setAutoRefresh(detail?.checked)}
								>
									Auto-refresh
								</Toggle>
							</Box>
							<ButtonDropdown
								mainAction={{
									text: 'Copy',
									iconName: 'copy',
									onClick: () => {
										navigate(
											{
												pathname: PAGES.CREATE_AD_HOC_QUERY.path,
												search: urlSearchString({ groupName }),
											},
											{
												state: {
													// Set default values
													selectQuery: adHocQuery?.selectQuery,
													clusterName: adHocQuery?.clusterName,
												},
											}
										);
									},
									disabled: !adHocQuery,
									disabledReason: 'Cannot copy query until it has loaded',
									loading: fetching || loading,
								}}
								items={[{ text: 'Export to activity', iconName: 'send', id: 'export' }]}
								onItemClick={({ detail }) => {
									switch (detail.id) {
										case 'export':
											navigate(
												{
													pathname: PAGES.CREATE_ACTIVITY.path,
													search: urlSearchString({ groupName }),
												},
												{
													state: {
														activityType: keys.ActivityType.EXTRACT,
														activityName: `activity_from_query_${queryId}`,
														selectQuery: adHocQuery?.selectQuery,
														clusterName: adHocQuery?.clusterName,
													},
												}
											);
											break;
										default:
											console.error('Received invalid action', detail);
									}
								}}
								disabled={!adHocQuery}
								disabledReason="Cannot complete actions until ad hoc query has loaded"
								loading={fetching || loading}
							/>
						</SpaceBetween>
					}
				>
					Query configuration
				</Header>
			}
		>
			<SpaceBetween size="m">
				<ColumnLayout columns={2}>
					<Content.Text
						{...contentProps}
						label="Query status"
						path="statusCode"
						viewTransform={transformTo.statusIndicator}
					/>
					<Content.Datetime {...contentProps} label="Create date" path="createDate" />
				</ColumnLayout>
				<AdHocContainerLayout queryId={queryId} mode={mode} />
			</SpaceBetween>
		</Container>
	);
}

type Item = { [key in string]: string };
function AdHocQueryOutputContainer(props: { queryId: string }) {
	const { queryId } = props;
	const groupName = useGroupName();
	const userAlias = useAlias();
	const { data: adHocQuery, fetching: fetchingQuery } = useAdHocQuery(queryId);
	const { data: adHocQueryOutput, fetching: fetchingOutput } = useAdHocQueryOutput(queryId);
	const queryCompleted = isQueryCompleted(adHocQuery?.statusCode);
	const [showLogs, setShowLogs] = useState(false);

	useConditionalEffect(() => {
		fetchAdHocQueryOutput({ groupName, userAlias, queryId }, true);
	}, [queryId, queryCompleted]);

	useConditionalEffect(() => {
		setShowLogs(adHocQuery?.statusCode === 'FAILURE');
	}, [queryCompleted]);

	return (
		<Container header={<Header variant="h2">Query output</Header>}>
			{queryCompleted ? (
				<SpaceBetween size="l">
					{adHocQuery.statusCode === 'SUCCESS' ? (
						<QueryDataTable data={adHocQueryOutput?.data} loading={fetchingQuery} />
					) : (
						<Alert type="error" header="Error message">
							{adHocQueryOutput?.errorMessage || <small>no error message found</small>}
						</Alert>
					)}
					<ExpandableSection
						headerText="Worker logs"
						expanded={showLogs}
						onChange={({ detail }) => setShowLogs(detail?.expanded)}
					>
						<LogsNavigator logs={adHocQueryOutput?.logs} loading={fetchingQuery || fetchingOutput} />
					</ExpandableSection>
				</SpaceBetween>
			) : (
				'Query output is only available once completed (status is SUCCESS or FAILURE)'
			)}
		</Container>
	);
}

function QueryDataTable(props: { data: AdHocQueryOutputData; loading?: boolean }) {
	const { preview, columns, datatypes, completeResultsUrl } = props.data ?? {};

	const items: Item[] = useMemo(
		() => preview?.map((row) => Object.fromEntries(row.map((val, ix) => [columns[ix], val]))) || [],
		[props.data]
	);

	const columnDefinitions = useMemo(
		() =>
			columns?.map((col, ix) => ({
				id: col,
				header: <DatatypePopover column={col} datatype={datatypes[ix]} />,
				cell: (item: Item) => item[col],
			})) || [],
		[props.data]
	);

	return (
		<Table
			header={
				<Box>
					<Header
						variant="h3"
						actions={
							completeResultsUrl && (
								<Button href={completeResultsUrl} iconName="download" variant="link">
									Download full .csv
								</Button>
							)
						}
					>
						Data preview
					</Header>
					<Box>
						This table only shows a preview of the first 100 rows returned by your query. You may download
						the complete query output as a .csv format above.
					</Box>
				</Box>
			}
			columnDefinitions={columnDefinitions}
			items={items}
			loading={props?.loading}
			loadingText="Retrieving results preview"
		/>
	);
}

function DatatypePopover(props: { column: string; datatype: string }) {
	return (
		<Popover triggerType="text" size="small" content={<code>{props.datatype}</code>} header="Datatype">
			{props.column}
		</Popover>
	);
}
